import type { FC } from 'react';

import { useFormatter } from '@eneco/i18n';
import { Bucket, Grid, InputDatepicker, InputUsage, Text } from '@eneco/sparky';
import { Controller, useFormContext } from 'react-hook-form';

import type { FormValues } from './types';
import type { CorrectionInvoiceFormContent } from '../correction-invoice-form-content.types';

type Props = {
  content: CorrectionInvoiceFormContent;
};

type InputUsageProps = Parameters<typeof InputUsage>[0];

type MeterReadingWrapperProps = {
  heading?: string;
  sharedOptions: Record<string, unknown>;
  startOptions: Record<string, unknown>;
  endOptions: Record<string, unknown>;
};

// One meter: its heading, then the start and/or end reading, depending on what the customer changes.
const MeterReadingWrapper: FC<MeterReadingWrapperProps> = ({ sharedOptions, startOptions, endOptions, heading }) => {
  const { watch } = useFormContext<FormValues>();
  const changeType = watch('changeType');
  return (
    <>
      <Text weight="bold">{heading}</Text>
      <Grid columns={{ initial: 1, md: 2 }} gap="6">
        {changeType !== 'end_result' ? (
          <InputUsage {...({ ...sharedOptions, ...startOptions } as unknown as InputUsageProps)} />
        ) : null}
        {changeType !== 'start_position' ? (
          <InputUsage {...({ ...sharedOptions, ...endOptions } as unknown as InputUsageProps)} />
        ) : null}
      </Grid>
    </>
  );
};

/** Mirrors the legacy `components/MeterReadings.tsx`. */
export const MeterReadings: FC<Props> = ({ content }) => {
  const {
    formFieldsMeterReadings_title,
    startDateFormField,
    endDateFormField,
    electricityLowLabel,
    electricityLowStartPositionFormField,
    electricityLowEndResultFormField,
    electricityNormalSingleMeterLabel,
    electricityNormalSingleMeterStartPositionFormField,
    electricityNormalSingleMeterEndResultFormField,
    gasLabel,
    gasStartPositionFormField,
    gasEndResultFormField,
    redeliveryLowLabel,
    redeliveryLowStartPositionFormField,
    redeliveryLowEndResultFormField,
    redeliveryNormalSingleMeterLabel,
    redeliveryNormalSingleMeterStartPositionFormField,
    redeliveryNormalSingleMeterEndResultFormField,
  } = content;

  const {
    register,
    formState: { errors },
    watch,
    control,
  } = useFormContext<FormValues>();

  const { date } = useFormatter();

  const changeType = watch('changeType');

  return (
    <Bucket title={formFieldsMeterReadings_title ?? ''}>
      <Bucket.Content>
        {changeType !== 'end_result' ? (
          <Controller
            control={control}
            name="readingStartDate"
            render={({ field: { onChange, value, name } }) => (
              <InputDatepicker
                name={name}
                isReadOnly
                label={startDateFormField?.label ?? ''}
                value={value}
                onDaySelect={d => onChange(d && date.short(d as Date))}
                fromYear={2000}
                toYear={new Date().getFullYear()}
                error={errors.readingStartDate?.message}
              />
            )}
          />
        ) : null}
        {changeType !== 'start_position' ? (
          <Controller
            control={control}
            name="readingEndDate"
            render={({ field: { onChange, value, name } }) => (
              <InputDatepicker
                name={name}
                isReadOnly
                label={endDateFormField?.label ?? ''}
                value={value}
                onDaySelect={d => onChange(d && date.short(d as Date))}
                fromYear={2000}
                toYear={new Date().getFullYear()}
                error={errors.readingEndDate?.message}
              />
            )}
          />
        ) : null}
        <MeterReadingWrapper
          heading={electricityLowLabel}
          sharedOptions={{ unit: 'kWh', type: 'energy', isOptional: true }}
          startOptions={{
            label: electricityLowStartPositionFormField?.label,
            ...register('electricityLowStart'),
            error: errors.electricityLowStart?.message,
          }}
          endOptions={{
            label: electricityLowEndResultFormField?.label,
            ...register('electricityLowEnd'),
            error: errors.electricityLowEnd?.message,
          }}
        />

        <MeterReadingWrapper
          heading={electricityNormalSingleMeterLabel}
          sharedOptions={{ unit: 'kWh', type: 'energy', isOptional: true }}
          startOptions={{
            label: electricityNormalSingleMeterStartPositionFormField?.label,
            ...register('electricityNormalStart'),
            error: errors.electricityNormalStart?.message,
          }}
          endOptions={{
            label: electricityNormalSingleMeterEndResultFormField?.label,
            ...register('electricityNormalEnd'),
            error: errors.electricityNormalEnd?.message,
          }}
        />

        <MeterReadingWrapper
          heading={redeliveryLowLabel}
          sharedOptions={{ unit: 'kWh', type: 'redelivery', isOptional: true }}
          startOptions={{
            label: redeliveryLowStartPositionFormField?.label,
            ...register('redeliveryLowStart'),
            error: errors.redeliveryLowStart?.message,
          }}
          endOptions={{
            label: redeliveryLowEndResultFormField?.label,
            ...register('redeliveryLowEnd'),
            error: errors.redeliveryLowEnd?.message,
          }}
        />

        <MeterReadingWrapper
          heading={redeliveryNormalSingleMeterLabel}
          sharedOptions={{ unit: 'kWh', type: 'redelivery', isOptional: true }}
          startOptions={{
            label: redeliveryNormalSingleMeterStartPositionFormField?.label,
            ...register('redeliveryNormalStart'),
            error: errors.redeliveryNormalStart?.message,
          }}
          endOptions={{
            label: redeliveryNormalSingleMeterEndResultFormField?.label,
            ...register('redeliveryNormalEnd'),
            error: errors.redeliveryNormalEnd?.message,
          }}
        />

        <MeterReadingWrapper
          heading={gasLabel}
          sharedOptions={{ unit: 'm3', type: 'gas', isOptional: true }}
          startOptions={{
            label: gasStartPositionFormField?.label,
            ...register('gasStart'),
            error: errors.gasStart?.message,
          }}
          endOptions={{
            label: gasEndResultFormField?.label,
            ...register('gasEnd'),
            error: errors.gasEnd?.message,
          }}
        />
      </Bucket.Content>
    </Bucket>
  );
};
